import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/* Read-side over the org's JobCache — Sprint 16c. Used by the campaign
 *  builder's JobPicker to bind a campaign to a JT job for {{job.*}}
 *  resolution. JT-sync is the refresh path — these endpoints don't make
 *  live JT calls. */

export interface Job {
  jt_job_id: string;
  org_id: string;
  title: string | null;
  location: string | null;
  status: string | null;
  attributes: Record<string, unknown>;
  last_synced_at: string;
}

export interface JobsListParams {
  q?: string;
  /** Defaults to "open-like" statuses on the server when omitted. */
  status?: string;
  limit?: number;
}

export function useJobs(params: JobsListParams = {}) {
  return useQuery<{ data: Job[] }>({
    queryKey: ['jobs', params],
    queryFn: async () => {
      const search: Record<string, string> = {};
      if (params.q) search.q = params.q;
      if (params.status) search.status = params.status;
      if (params.limit) search.limit = String(params.limit);
      const { data } = await apiClient.get<{ data: Job[] }>('/jobs', { params: search });
      return data;
    },
    // Search results refresh on every keystroke — keep them fresh.
    staleTime: 10_000,
  });
}

export function useJob(jtJobId: string | null | undefined) {
  return useQuery<{ data: Job }>({
    queryKey: ['jobs', jtJobId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: Job }>(`/jobs/${jtJobId}`);
      return data;
    },
    enabled: !!jtJobId,
  });
}
